import React, { useEffect, useState } from 'react';
import { DollarSign, Loader2, TrendingUp, Wallet, Receipt } from 'lucide-react';
import { api } from '../../services/api';

const STATUS_LABELS = {
  pending: 'Offen',
  open: 'Offen',
  booked: 'Gebucht',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

const STATUS_COLORS = {
  pending: 'bg-blue-100 text-blue-700',
  open: 'bg-blue-100 text-blue-700',
  booked: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

export function DriverEarningsTab({ t, formatDate }) {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/driver/earnings');
        setEarnings(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 flex items-center justify-center" data-testid="earnings-loading">
        <Loader2 size={24} className="animate-spin text-orange-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <p className="text-sm text-rose-600">{error}</p>
      </div>
    );
  }
  if (!earnings) return null;

  const transfers = earnings.transfers || [];

  return (
    <div className="space-y-5" data-testid="driver-earnings-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card icon={TrendingUp} label="Brutto verdient" value={`${earnings.total_gross || 0} EUR`} bg="bg-blue-50 border-blue-100" color="text-blue-800" iconColor="text-blue-500" testid="my-total-gross" />
        <Card icon={Receipt} label={`Portal-Kommission (${earnings.commission_percent ?? 15}%)`} value={`−${earnings.total_commission || 0} EUR`} bg="bg-rose-50 border-rose-100" color="text-rose-800" iconColor="text-rose-500" testid="my-total-commission" />
        <Card icon={Wallet} label="Netto-Anspruch" value={`${earnings.total_net || 0} EUR`} bg="bg-indigo-50 border-indigo-100" color="text-indigo-800" iconColor="text-indigo-500" testid="my-total-net" />
        <Card icon={DollarSign} label="Bereits ausgezahlt" value={`${earnings.total_paid || 0} EUR`} bg="bg-emerald-50 border-emerald-100" color="text-emerald-800" iconColor="text-emerald-500" testid="my-total-paid" />
        <Card icon={DollarSign} label="Offener Betrag" value={`${earnings.balance_owed || 0} EUR`} bg={(earnings.balance_owed || 0) > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'} color={(earnings.balance_owed || 0) > 0 ? 'text-orange-800' : 'text-slate-600'} iconColor={(earnings.balance_owed || 0) > 0 ? 'text-orange-500' : 'text-slate-400'} testid="my-balance-owed" />
      </div>

      {/* Mobile cards / Desktop table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Alle Transfers ({transfers.length})</h3>
        </div>

        {transfers.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <DollarSign size={36} className="mx-auto mb-3 text-slate-200" />
            <p className="text-sm">Noch keine abgeschlossenen Buchungen.</p>
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <Th>Kunde</Th><Th>Strecke</Th><Th>Datum</Th><Th>Status</Th>
                    <Th right>Brutto</Th><Th right>Kommission</Th><Th right>Netto</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transfers.map(tr => (
                    <tr key={tr.offer_id} className="hover:bg-slate-50" data-testid={`earnings-row-${tr.offer_id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{tr.customer_name || '-'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{tr.pickup} → {tr.dropoff}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate ? formatDate(tr.pickup_date) : tr.pickup_date}</td>
                      <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[tr.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[tr.status] || tr.status}</span></td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800">{tr.price} {tr.currency}</td>
                      <td className="px-4 py-3 text-right text-rose-600">−{tr.commission ?? 0} {tr.currency}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">{tr.net ?? tr.price} {tr.currency}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td colSpan="4" className="px-4 py-3 text-xs text-slate-700">Gesamt</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-800">{earnings.total_gross || 0} EUR</td>
                    <td className="px-4 py-3 text-right text-sm text-rose-700">−{earnings.total_commission || 0} EUR</td>
                    <td className="px-4 py-3 text-right text-sm text-emerald-700">{earnings.total_net || 0} EUR</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-slate-100">
              {transfers.map(tr => (
                <div key={tr.offer_id} className="p-4" data-testid={`earnings-card-${tr.offer_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{tr.customer_name || '-'}</p>
                      <p className="text-xs text-slate-500 truncate">{tr.pickup} → {tr.dropoff}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{formatDate ? formatDate(tr.pickup_date) : tr.pickup_date}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${STATUS_COLORS[tr.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[tr.status] || tr.status}</span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 space-y-1 text-xs">
                    <Row label="Brutto" value={`${tr.price} ${tr.currency}`} />
                    <Row label={`Kommission (${tr.commission_percent}%)`} value={`−${tr.commission ?? 0} ${tr.currency}`} valueClass="text-rose-600" />
                    <Row label="Netto" value={`${tr.net ?? tr.price} ${tr.currency}`} valueClass="text-emerald-700 font-bold" />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, bg, color, iconColor, testid }) {
  return (
    <div className={`rounded-xl p-4 border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} className={iconColor} />
        <p className="text-xs font-medium text-slate-600 truncate">{label}</p>
      </div>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

function Th({ children, right }) {
  return <th className={`px-4 py-3 text-xs font-semibold text-slate-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>;
}

function Row({ label, value, valueClass = 'text-slate-800' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}
